import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import FlagSummary from './FlagSummary';
import ThresholdNote from './ThresholdNote';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// Refunds (Requirement Document v1.0 S4.15). One row per person who recorded a refund this month - their refund count and total
// value. Since 2026-10-08 it has ONE company-wide rupee threshold (RefundsConfig.jsx): a person whose refunds add up to MORE than
// it in the month is flagged. The backend query already only returns employees with at least one refund - nothing extra needed to
// enforce "show only when Refund Count >= 1".
const RefundsDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null); // the real totals the home card also shows
  const [thresholdNote, setThresholdNote] = useState(null); // why there is no threshold for this month, when there isn't
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/refunds-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty table saying "No refunds this period", which is false
        if (res?.status !== 'success') {
          setRows([]);
          setSummary(null);
          setError(res?.message || 'Could not load Refunds');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalCount: res?.data?.totalCount ?? 0, totalValue: res?.data?.totalValue, people: res?.data?.totalEvaluated ?? 0, warningCount: res?.data?.warningCount ?? 0 });
        setThresholdNote(res?.data?.thresholdNote || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Refunds');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  // flagged first, the biggest overshoot at the top; everyone else by refund value
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (Number(b.isWarning) - Number(a.isWarning)) || (b.refundValue - a.refundValue)),
    [rows]
  );

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'refundCount', headerName: 'Refund Count', align: 'right' },
    {
      field: 'refundValue',
      headerName: 'Refund Value',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {`₹${Number(row.refundValue).toLocaleString()}`}
        </Typography>
      ),
      csvValue: (row) => row.refundValue,
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => (row.threshold === null
        ? <Typography variant="body2" color="text.secondary">{row.thresholdOff ? 'Switched off' : 'Not configured'}</Typography>
        : `> ₹${Number(row.threshold).toLocaleString()}`),
      csvValue: (row) => (row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : `> ₹${Number(row.threshold).toLocaleString()}`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Refunds"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('refunds-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/refunds-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && summary && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          <strong>{Number(summary.totalCount).toLocaleString('en-IN')}</strong> {summary.totalCount === 1 ? 'refund' : 'refunds'}
          {summary.totalValue != null ? ` · ₹${Number(summary.totalValue).toLocaleString('en-IN')}` : ''} by {summary.people} {summary.people === 1 ? 'person' : 'people'}
        </Typography>
      )}

      {!loading && !error && summary && <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.people} unit=" people" />}
      {!loading && !error && <ThresholdNote note={thresholdNote} month={month} configPath="refunds-config" canSet={canConfigure('refunds-config')} />}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No refunds this period."
          footer={(shown) => [
            {
              key: 'total',
              first: true,
              emphasis: true,
              cells: {
                entityName: shown.length === rows.length ? 'Total' : 'Total of the rows shown',
                refundCount: shown.reduce((t, r) => t + Number(r.refundCount || 0), 0).toLocaleString('en-IN'),
                refundValue: `₹${shown.reduce((t, r) => t + Number(r.refundValue || 0), 0).toLocaleString('en-IN')}`,
              },
            },
          ]}
          exportFileName="Refunds"
        />
      )}
    </WarningPage>
  );
};

export default RefundsDashboard;
